import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2, X, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ContentManager() {
  const [faqs, setFaqs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [siteRes, settingsRes] = await Promise.all([api.get("/site"), api.get("/admin/settings")]);
      setFaqs(siteRes.data.faqs);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveFaq = async (e) => {
    e.preventDefault();
    const payload = { question: editingFaq.question, answer: editingFaq.answer, order: Number(editingFaq.order) || 0 };
    try {
      if (editingFaq.id) {
        await api.put(`/admin/faqs/${editingFaq.id}`, payload);
        toast.success("FAQ updated.");
      } else {
        await api.post("/admin/faqs", payload);
        toast.success("FAQ added.");
      }
      setEditingFaq(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeFaq = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await api.delete(`/admin/faqs/${id}`);
      toast.success("FAQ deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved. The website contact details are now updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading || !settings) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-deep animate-spin" /></div>;

  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div data-testid="content-manager" className="grid lg:grid-cols-2 gap-8 items-start">
      <div>
        <div className="flex justify-between items-center mb-5">
          <h2 className="font-display text-xl font-extrabold text-brand-deep">FAQs ({faqs.length})</h2>
          <button onClick={() => setEditingFaq({ question: "", answer: "", order: faqs.length + 1 })} data-testid="faq-new-button" className="flex items-center gap-2 rounded-full bg-brand-deep px-4 py-2 text-xs font-bold text-white hover:bg-brand-forest transition-all">
            <Plus className="w-3.5 h-3.5" /> Add FAQ
          </button>
        </div>

        {editingFaq && (
          <form onSubmit={saveFaq} data-testid="faq-editor-form" className="bg-white rounded-2xl border border-brand-gold/50 p-5 mb-4 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-display font-bold text-brand-deep text-sm">{editingFaq.id ? "Edit FAQ" : "New FAQ"}</h3>
              <button type="button" onClick={() => setEditingFaq(null)} className="text-brand-charcoal/40 hover:text-brand-deep"><X className="w-4 h-4" /></button>
            </div>
            <input required className="input-field !py-2.5 text-sm" placeholder="Question" value={editingFaq.question} onChange={(e) => setEditingFaq((f) => ({ ...f, question: e.target.value }))} />
            <textarea required rows={3} className="input-field text-sm" placeholder="Answer" value={editingFaq.answer} onChange={(e) => setEditingFaq((f) => ({ ...f, answer: e.target.value }))} />
            <div className="flex items-center gap-3">
              <input type="number" className="input-field !py-2 text-xs w-24" placeholder="Order" value={editingFaq.order} onChange={(e) => setEditingFaq((f) => ({ ...f, order: e.target.value }))} />
              <button type="submit" data-testid="faq-save-button" className="flex items-center gap-2 rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold text-white hover:bg-brand-forest transition-all">
                <Save className="w-3.5 h-3.5" /> Save FAQ
              </button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.id} data-testid={`admin-faq-${i}`} className="bg-white rounded-2xl border border-brand-deep/10 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-brand-deep text-sm">{f.question}</h3>
                  <p className="mt-1 text-xs text-brand-charcoal/55 leading-relaxed">{f.answer}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button onClick={() => setEditingFaq({ ...f })} data-testid={`faq-edit-${i}`} className="w-8 h-8 rounded-full bg-brand-deep/5 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removeFaq(f.id)} data-testid={`faq-delete-${i}`} className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-red-400 hover:bg-red-500 hover:text-white transition-all">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={saveSettings} data-testid="settings-form" className="bg-white rounded-3xl border border-brand-deep/10 p-6 sm:p-8 space-y-4">
        <h2 className="font-display text-xl font-extrabold text-brand-deep">Business Settings</h2>
        <p className="text-xs text-brand-charcoal/50 leading-relaxed">
          These details power every Call, WhatsApp and Email button across the website.
        </p>
        <div>
          <label className="label-field">Phone (display format)</label>
          <input className="input-field" data-testid="settings-phone-input" value={settings.phone || ""} onChange={setS("phone")} placeholder="+91 80101 56588" />
        </div>
        <div>
          <label className="label-field">WhatsApp Number (country code, digits only)</label>
          <input className="input-field" data-testid="settings-whatsapp-input" value={settings.whatsapp || ""} onChange={setS("whatsapp")} placeholder="918010156588" />
        </div>
        <div>
          <label className="label-field">Email</label>
          <input type="email" className="input-field" data-testid="settings-email-input" value={settings.email || ""} onChange={setS("email")} />
        </div>
        <div>
          <label className="label-field">Registered Office Address</label>
          <textarea rows={2} className="input-field" value={settings.address || ""} onChange={setS("address")} />
        </div>
        <div>
          <label className="label-field">Regional Office Address (Bihar)</label>
          <textarea rows={2} className="input-field" data-testid="settings-regional-address-input" value={settings.regional_address || ""} onChange={setS("regional_address")} />
        </div>
        <div>
          <label className="label-field">Regional Operations Text</label>
          <textarea rows={2} className="input-field" value={settings.region_text || ""} onChange={setS("region_text")} />
        </div>
        <button type="submit" disabled={savingSettings} data-testid="settings-save-button" className="rounded-full bg-brand-deep px-7 py-3 text-sm font-bold text-white hover:bg-brand-forest transition-all disabled:opacity-60 flex items-center gap-2">
          {savingSettings && <Loader2 className="w-4 h-4 animate-spin" />} Save Settings
        </button>
      </form>
    </div>
  );
}
